<!--
	@name app-air-academy
	@description Air Academy page
	@date 2020/02/21
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name" class="pa-3">
		<v-sheet class="pa-6 mb-2 rounded background-gradient">
			<v-row no-gutters>
				<v-col class="shrink">
					<div class="align-self-center image-content">
						<img class="brand-logo" :src="'logo/brand/air_academy_logo_neg.svg' | urlize" />
					</div>
				</v-col>
				<v-col class="grow text-right">
					<app-common-dialog-glossary
						class="d-inline"
						:items="glossaryItems"
						:button-text-hidden="true"
						:icon-large="true"
						icon-color="white"
						title="Frequently asked questions"
					/>
					<v-tooltip bottom>
						<template #activator="{ on }">
							<v-btn large text color="white" to="/account" v-on="on">
								<v-icon large>
									mdi-backburger
								</v-icon>
							</v-btn>
						</template>
						<span>Back to Dashboard</span>
					</v-tooltip>
				</v-col>
			</v-row>
		</v-sheet>

		<v-row no-gutters class="mx-n3 mt-9 px-3" style="position: relative">
			<a
				v-if="recommendations.length"
				:class="['text-body-1', 'font-weight-medium', { 'warning--text': outstandingRecommendations, 'grey--text': !outstandingRecommendations }]"
				style="position: absolute; right: 32px; top: 0"
				@click="$refs.recommendationsDialog.open()"
			>
				Recommendations
				<v-badge :color="outstandingRecommendations ? 'warning' : 'grey'" :content="outstandingRecommendations.toString()" inline style="position: relative; bottom: 2px" />
			</a>
			<v-col cols="10">
				<div class="border-effect border-effect--lg ml-6">
					<p class="text-h5 mb-2 font-weight-regular">
						<span class="font-weight-bold">Air Academy: </span> We live & breathe your development
					</p>
					<p class="text-body-1 mb-0">
						Welcome to your Air Academy Dashboard, an innovative learning platform that gives you access to LIBF-accredited training programmes and continuous
						professional development opportunities, which cover a range of critical areas to help enhance your industry knowledge.
					</p>
				</div>
			</v-col>
			<v-col cols="2" />
		</v-row>

		<v-row class="mt-6">
			<v-col class="py-0" cols="12" md="8">
				<common-base-tab-view :tabs="tabs" router-view />
			</v-col>
			<v-col class="py-0" cols="12" md="4">
				<app-air-academy-status class="mt-6" />
				<app-air-academy-cpd class="mt-6" />
			</v-col>
		</v-row>

		<common-dialog ref="recommendationsDialog">
			<template #body>
				<div class="border-effect border-effect--grey">
					<p class="text-h6 font-weight-medium mb-1">
						Recommendations ({{ outstandingRecommendations }})
					</p>
					<p class="text-body-2 font-weight-regular mb-6">
						Earn more as you grow. There's so much more to gain by working with Air, your club membership is just the start...
					</p>
				</div>
				<v-card class="mx-auto" elevation="0">
					<v-list color="grey lighten-2" class="pa-0">
						<v-list-item-group v-model="activeRecommendations" active-class="pink--text" multiple>
							<template v-for="(recommendation, index) in recommendations">
								<v-list-item
									:key="recommendation.title"
									:value="recommendation.id"
									:disabled="activeRecommendations.includes(recommendation.id)"
									@click="recommendationUpdate(recommendation.id)"
								>
									<template #default="{ active }">
										<v-list-item-content>
											<v-list-item-title>
												{{ recommendation.title }}
											</v-list-item-title>
										</v-list-item-content>

										<v-list-item-action>
											<v-icon v-if="!active" color="grey lighten-1">
												mdi-check-bold
											</v-icon>

											<v-icon v-else color="success">
												mdi-check-bold
											</v-icon>
										</v-list-item-action>
									</template>
								</v-list-item>

								<v-divider v-if="index < recommendations.length - 1" :key="index" />
							</template>
						</v-list-item-group>
					</v-list>
				</v-card>
			</template>
		</common-dialog>

		<common-dialog ref="onboarding" @closed="onboardingClosed">
			<template #header>
				<p class="text-h6 font-weight-medium mb-0">
					Welcome to your new Academy page
				</p>
			</template>
			<template #body>
				<p class="text-body-1 font-weight-bold mb-3">
					The Air Academy is now free to all Air members, giving you easier access to learning, development and events designed to support your professional growth.
				</p>
				<p class="text-body-1 mb-5">
					Click the video to discover what’s changed, explore what’s now available, and learn how to make the most of your development through the Academy.
				</p>
				<div class="d-block rounded" style="padding:56.25% 0 0 0;position:relative; overflow: hidden;">
					<iframe id="vimeo-onboarding" :src="`https://player.vimeo.com/video/1204786728?h=6ee8be85f8&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`" frameborder="0" allow="autoplay;fullscreen; picture-in-picture; clipboard-write; encrypted-media" style="position:absolute;top:0;left:0;width:100%;height:100%;" />
				</div>
			</template>
		</common-dialog>

		<app-air-academy-dashboard-ring
			ref="ringOne"
			:ring="1"
			:catalog-id="25"
			:title="'Adviser Accreditation for Later Life Lending'"
			:testimonial="{
				name: 'Adviser Accreditation for Later Life Lending',
				image: `${VUE_APP_S3_AIR_ASSETS_URL}/webinar/AcademyAccreditation.jpg`,
				mimetype: 'video/mp4',
				hideStatus: true,
				fixedHeight: true,
				emit: 'play-vimeo'
			}"
			:vimeo="{ source:'1204786726', h:'de6a778727' }"
		>
			<template #logo>
				<img :src="'LIBF_Accredited Learning Prog_BlackRust.png' | urlize('brand')" class="d-block" style="width: 100%; max-width: 300px; float: right;" />
			</template>
			<template #highlight>
				<v-alert max-width="400" dense icon="mdi-certificate" type="info" colored-border border="left" color="accent" class="grey lighten-3 mb-6 ml-3">
					24 hours structured CPD
				</v-alert>
			</template>
			<template #copy>
				<p class="text-body-1 mb-3">
					Our Adviser Accreditation for Later Life Lending programme provides our members with the opportunity to achieve professional status along with a bespoke digital badge of achievement that stands as a kite mark of industry excellence.
				</p>
				<p class="text-body-1 mb-0">
					Our Academy membership is recognised as representing the gold standard of later life lending advice quality and customer outcomes, attracting higher procuration fees from our Ambassador lending partners and boosting the new business growth potential for those who attain and maintain their professional status.
				</p>
			</template>
		</app-air-academy-dashboard-ring>

		<app-air-academy-dashboard-ring
			ref="ringTwo"
			:ring="2"
			:catalog-id="26"
			:title="'Later Life Lending Adviser Accelerator'"
			:testimonial="{
				name: 'Later Life Lending Adviser Accelerator',
				image: `${VUE_APP_S3_AIR_ASSETS_URL}/webinar/AcademyInduction.jpg`,
				mimetype: 'video/mp4',
				hideStatus: true,
				fixedHeight: true,
				emit: 'play-vimeo'
			}"
			:vimeo="{ source:'1204841086', h:'93735151d4' }"
		>
			<template #logo>
				<img :src="'LIBF_Accredited Learning Prog_BlackRust.png' | urlize('brand')" class="d-block" style="width: 100%; max-width: 300px; float: right;" />
			</template>
			<template #highlight>
				<v-alert max-width="400" dense icon="mdi-certificate" type="info" colored-border border="left" color="accent" class="grey lighten-3 mb-6 ml-3">
					24 hours structured CPD
				</v-alert>
			</template>
			<template #copy>
				<p class="text-body-1 mb-3">
					The modules below are On Demand Learning units designed to complement the live portion of the LIBF-accredited Later Life Lending Adviser Accelerator Course.
				</p>
				<p class="text-body-1 mb-3">
					To receive the full 24 hours of structured CPD, advisers must complete both the On Demand Learning units and the face-to-face facilitation element of the course.
				</p>
				<p class="text-body-1 mb-3">
					For more information on how to book onto a live accelerator, please <a href="mailto:academy.enquiries@airlaterlife.co.uk">click here</a>.
				</p>
			</template>
		</app-air-academy-dashboard-ring>

		<app-air-academy-dashboard-ring
			ref="ringFour"
			:restricted="false"
			:ring="4"
			:catalog-id="35"
			:title="'On Demand Learning & Content'"
			:testimonial="{
				name: 'On Demand Learning & Content',
				image: `${VUE_APP_S3_AIR_ASSETS_URL}/webinar/AcademyCPD.jpg`,
				mimetype: 'video/mp4',
				hideStatus: true,
				fixedHeight: true,
				emit: 'play-vimeo'
			}"
			:vimeo="{ source:'1204786727', h:'7e37fdb284' }"
		>
			<template #copy>
				<p class="text-body-1 mb-3">
					Access flexible resources designed to help you stay informed, confident and supported in a fast-moving later life lending market.
				</p>
				<p class="text-body-1 mb-3">
					Explore content across Air systems support, On Demand learning, Marketing support, Live event recording and podcasts — from practical system guidance and marketing support to event catch-ups, focused learning and expert insight on the go.
				</p>
				<p class="text-body-1 mb-0">
					With on demand resources and a personalised CPD tracker to evidence your progress, developing your knowledge and skills has never been easier.
				</p>
			</template>
		</app-air-academy-dashboard-ring>

		<app-air-academy-dashboard-events
			ref="ringFive"
			:title="'Air Academy Events'"
		/>
	</div>
</template>

<script>
	const ONBOARDING_DIALOG = 'academyOnboarding072026';

	import AppAirAcademyCpd from '@/component/app/air-academy/cpd';
	import AppAirAcademyDashboardEvents from '@/component/app/air-academy/dashboard/events';
	import AppAirAcademyDashboardRing from '@/component/app/air-academy/dashboard/ring';
	import AppAirAcademyStatus from '@/component/app/air-academy/status';
	import CommonBaseTabView from '@/component/common/base/tab-view';
	import CommonDialog from '@/component/common/dialog';
	import AppCommonDialogGlossary from '@/component/common/dialog/glossary';
	import { ElementTools, EventBus } from '@/utils';
	import { mapActions, mapGetters, mapState } from 'vuex';
	import { $localStorage } from '@/utils/storage';

	export default {
		name: 'app-air-academy',

		components: {
			CommonBaseTabView,
			CommonDialog,
			AppCommonDialogGlossary,
			AppAirAcademyStatus,
			AppAirAcademyCpd,
			AppAirAcademyDashboardRing,
			AppAirAcademyDashboardEvents
		},

		data() {
			return {
				VUE_APP_S3_AIR_ASSETS_URL: process.env.VUE_APP_S3_AIR_ASSETS_URL,
				tabs: [
					{ title: 'Overview', alerts: 0, name: 'airAcademy.dashboard', path: '/academy/dashboard' }
				],
				glossaryItems: [
					{
						key: '1',
						type: 'text',
						title: 'Why join the Academy?',
						description:
							'As the later life lending market continues to evolve, advisers are expected to demonstrate high standards of knowledge, professionalism and care, particularly when supporting older consumers through complex financial decisions.<br/><br/>Air Academy has been designed to help advisers build and evidence that expertise. Through sector-leading training, practical learning and ongoing development, the Academy supports advisers in enhancing their industry knowledge, strengthening their advice standards and meeting their continuing professional development requirements.<br/><br/>The Academy is widely recognised as a benchmark for later life lending advice quality and customer outcomes. Advisers who achieve and maintain professional status through the Academy may also benefit from enhanced procuration fees from participating ambassador lending partners, as well as increased opportunities to grow their new business.'
					},
					{
						key: '2',
						type: 'text',
						title: 'What are the four sections of the Academy?',
						description:
							'The Academy is made up of four key sections:<br/><br/><strong>Section 1:</strong> Adviser Accreditation for Later Life Lending Programme<br/><strong>Section 2:</strong> Later Life Lending Adviser Accelerator<br/><strong>Section 3:</strong> On Demand Learning & Content<br/><strong>Section 4:</strong> Air Academy Events'
					},
					{
						key: '3',
						type: 'text',
						title: 'What qualification do I need to start the training?',
						description: 'To start either of our accredited programmes, you need to hold CeMAP and CeRER/ER1, or equivalent qualifications.'
					},
					{
						key: '4',
						type: 'text',
						title: 'Who are the programmes accredited by?',
						description:
							'The Adviser Accreditation for Later Life Lending Programme, the Later Life Lending Adviser Accelerator and the Fact Find Learning Programme are accredited by the London Institute of Banking & Finance, now part of Walbrook.'
					},
					{
						key: '5',
						type: 'text',
						title: 'What is Trusted Partner Status?',
						description: 'Trusted Partner Status is formal recognition from the London Institute of Banking & Finance, part of Walbrook, awarded to organisations that demonstrate high standards in education, training and learner support.<br/><br/>Air Academy has achieved Trusted Partner Status in recognition of the quality of its training programmes, the expertise of its learning consultants and the support provided to advisers throughout their learning journey.<br/><br/>This recognition reflects Air Academy’s long-standing commitment to raising standards across the later life lending sector and reinforces its position as a leading provider of specialist adviser training.'
					},
					{
						key: '6',
						type: 'text',
						title: 'Who is the Adviser Accreditation for Later Life Lending for?',
						description:
							'The Adviser Accreditation for Later Life Lending is designed for all later life lending advisers, whether you are new to the market or have been advising for some time.<br/><br/>The programme helps advisers revisit the essential areas of the advice process, refresh their knowledge and stay up to date as the market, regulation and customer expectations continue to evolve.'
					},
					{
						key: '7',
						type: 'text',
						title: 'What are the 10 modules included in the Adviser Accreditation for Later Life Lending?',
						description:
							'The programme includes 10 modules:<br/><br/>The Later Life Lending Products<br/>The Later Life Lending Process<br/>Later Life Lending in the Broader Context<br/>Building a Client Proposition<br/>The Market<br/>The Industry</br>The Client<br/>Soft Skills<br/>Client Vulnerability<br/>Affordability<br/>'
					},
					{
						key: '8',
						type: 'text',
						title: 'How does an adviser achieve professional status through the Academy?',
						description:
							'To achieve professional status, advisers must complete the 10 LIBF-accredited modules within the Adviser Accreditation for Later Life Lending Programme.<br/><br/>Completion of the programme counts towards <strong>24 hours of structured CPD.</strong>'
					},
					{
						key: '9',
						type: 'text',
						title: 'What does an adviser get for achieving professional status?',
						description:
							'Once you complete the 10 LIBF-accredited modules, you can display a digital badge in your email signature and approved promotional materials. This demonstrates that you have achieved a recognised level of competence through an LIBF-accredited programme.<br/><br/>Advisers who achieve professional status may also gain access to enhanced procuration fees from participating lending partners.'
					},
					{
						key: '10',
						type: 'text',
						title: 'Do I have to retake the modules every year to maintain my accreditation?',
						description: 'No. You do not need to retake all modules each year.<br/><br/>To maintain your accreditation, you only need to complete any new required modules and pass a re-accreditation MOT, which is based on the existing programme content.'
					},
					{
						key: '11',
						type: 'text',
						title: 'Who is the Later Life Adviser Accelerator for?',
						description:
							'The Later Life Lending Adviser Accelerator is designed for later life lending advisers who want to strengthen their advice process and deliver good customer outcomes.<br/><br/>Covering the full customer journey, the programme explores how the adviser role has evolved since the introduction of the FCA’s Consumer Duty. It provides practical guidance on structuring customer appointments, using Air’s tools to support comprehensive customer conversations and considering wider areas that may influence later life decisions, such as care, estate planning and broader financial needs.'
					},
					{
						key: '12',
						type: 'text',
						title: 'Why should I complete the Accelerator rather than learn as I go?',
						description:
							'Later life lending advice requires careful consideration of a customer’s wider circumstances, needs and objectives. If an adviser fails to consider those needs fully, or does not clearly evidence why their recommendation is suitable and likely to deliver a good outcome, there is a risk that the customer could experience avoidable harm.<br/><br/>Where that harm was foreseeable, the adviser may also face regulatory scrutiny.<br/><br/>By completing the Later Life Lending Adviser Accelerator, advisers have the opportunity to explore the full scope of their role, understand their responsibilities in more depth and consider the range of options available to customers. It helps advisers start with confidence, strengthen their advice process and put good customer outcomes at the centre of every recommendation.'
					}
				],
				activeRecommendations: [],
				recommendations: [],
				cpdTrainingItems: []
			};
		},

		computed: {
			...mapState('Account', ['user']),
			...mapState('AppWebinar', ['authStatus', 'courses', 'catalogs']),
			...mapGetters('Account', ['membership', 'tier']),
			...mapGetters('CmsForm', ['form']),
			...mapGetters('CmsData', ['data']),

			outstandingRecommendations() {
				return this.recommendations.length - this.activeRecommendations.length;
			}
		},

		watch: {
			'$route.name': {
				handler(name) {
					if (name === 'airAcademy') this.$router.push('/academy/dashboard');
				},
				deep: true,
				immediate: true
			}
		},

		created() {
			EventBus.$on('open-academy-ring', this.openRing);
			EventBus.$on('credentials-updated', this.credentialsUpdated);
		},

		destroyed() {
			EventBus.$off('open-academy-ring', this.openRing);
			EventBus.$off('credentials-updated', this.credentialsUpdated);
		},

		mounted() {
			this.fetchCourseList();
			this.fetchCatalogContent();
            if (!$localStorage.isDialogHidden(ONBOARDING_DIALOG)) {
                this.$refs['onboarding'].open();
                $localStorage.hideDialog(ONBOARDING_DIALOG);
            }
			//TODO turn recommendations back on when list is finalised
			/*this.getUserRecommendation(this.user.id).then(({ data }) => {
				this.recommendations = data[this.tier.id || 'none'];
				this.activeRecommendations = this.recommendations.filter((recommendation) => recommendation.user_selected).map((recommendation) => recommendation.id);
			});*/
		},

		methods: {
			...mapActions('CmsDownload', ['loadDownloadsList']),
			...mapActions('CmsForm', ['loadForm']),
			...mapActions('CmsData', ['loadData']),
			...mapActions('AccountUserRecommendation', ['getUserRecommendation', 'addUserRecommendation', 'deleteUserRecommendation']),
			...mapActions('AppWebinar', ['getCourseList', 'getCatalogContent']),

			/**
			 * @name fetchCourseList
			 * @description Fetch the course list from the API
			 */
			fetchCourseList() {
				ElementTools.fireNotification(this.$el, 'info', {
					text: 'Retrieving your LMS account details',
					timeout: 0,
					close: true
				});

				this.getCourseList()
					.then(() => {
						ElementTools.fireNotification(this.$el, 'success', {
							text: 'Success',
							timeout: 3000,
							close: true
						});
					})
					.catch((error) => {
						if ([400].includes(error?.status))
							ElementTools.fireNotification(this.$el, 'error', {
								text: 'An error occurred whilst attempting to connect to your air academy learning platform account. Please click "connect to my air academy learning platform" to resolve this issue.',
								timeout: 0,
								close: true
							});
						else if ([500].includes(error?.status))
							ElementTools.fireNotification(this.$el, 'error', {
								text: 'An unexpected error occurred whilst attempting to connect to your air academy learning platform account. Please speak with our support team to resolve this issue.',
								timeout: 0,
								close: true
							});
						else ElementTools.hideNotification(this.$el);
					});
			},

			fetchCatalogContent() {
				// Accreditation
				this.getCatalogContent(25)
					.then(() => {})
					.catch((error) => { console.log(error) });
				// Accelerator
				this.getCatalogContent(26)
					.then(() => {})
					.catch((error) => { console.log(error) });
				// Systems
				this.getCatalogContent(28)
					.then(() => {})
					.catch((error) => { console.log(error) });
				// CPD
				this.getCatalogContent(35)
					.then(() => {})
					.catch((error) => { console.log(error) });
			},

			credentialsUpdated() {
				this.fetchCourseList();
				this.fetchCatalogContent();
			},

			openRing(ring) {
				this.$refs[ring].open();
			},

			navigateToCourse() {
				window.location.href = 'https://academy.airlaterlife.co.uk/learn/course/internal/view/classroom/216/air-academy-later-life-adviser-accelerator-welcome-webinar';
			},

            onboardingClosed() {
                const iframe = document.querySelector('#vimeo-onboarding');
                const player = new Vimeo.Player(iframe); //Vimeo class available through https://player.vimeo.com/api/player.js
                player.pause().catch(function(error) {
                    console.error("Error pausing video:", error);
                });
            }
		}
	};
</script>

<style lang="scss" scoped>
	.image-box {
		background: #e8e8eb;
	}

	.brand-logo {
		max-height: 40px;
	}

	.mfg2020 {
		height: 250px;
	}

	.image-content {
		font-size: 0;
	}

	.brand-logo {
		height: 40px;
	}
</style>
